import React, { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Download, LogOut, MonitorSmartphone, ShieldCheck, Trash2 } from "lucide-react";

import { api } from "@/lib/api";
import { Button, ScreenHeader, Spinner } from "@/components/kit";
import { Page } from "@/components/layout";
import { Field, Section } from "@/admin/kit";
import { useApp } from "@/store";

const PW_HINT = "At least 8 characters, with a letter and a number.";
const strong = (p) => p.length >= 8 && /[a-z]/i.test(p) && /\d/.test(p);

export default function Account() {
  const navigate = useNavigate();
  const { ready, user, mode, toast, applyUser, applySession, logout, deleteAccount } = useApp();
  const [name, setName] = useState(user?.name ?? "");
  useEffect(() => {
    if (user) setName(user.name ?? "");
  }, [user]);
  const [email, setEmail] = useState("");
  const [emailPw, setEmailPw] = useState("");
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(null);
  const [exported, setExported] = useState(null);
  const [delStep, setDelStep] = useState(0);
  const [delInput, setDelInput] = useState("");
  const [delError, setDelError] = useState(null);
  const leaving = useRef(false);
  if (!ready) return <div data-testid="account-loading" className="flex justify-center p-12"><Spinner /></div>;
  if (mode !== "user" || !user) return <Navigate to={leaving.current ? "/" : "/privacy"} replace />;

  const run = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(null);
    }
  };
  const saveName = () => run("name", async () => {
    applyUser(await api("/me/account", { method: "PUT", body: { name: name.trim() } }));
    toast("Name updated.", "success");
  });
  const changeEmail = () => run("email", async () => {
    await applySession(await api("/me/email", { method: "POST", body: { new_email: email.trim(), current_password: emailPw } }));
    setEmail("");
    setEmailPw("");
    toast("Email updated.", "success");
  });
  const changePw = () => run("pw", async () => {
    await applySession(await api("/me/password", { method: "POST", body: { current_password: pw.current, new_password: pw.next } }));
    setPw({ current: "", next: "", confirm: "" });
    toast("Password changed. Other devices were signed out.", "success");
  });
  const signOutAll = () => run("all", async () => {
    await api("/auth/logout-all", { method: "POST" });
    leaving.current = true;
    await logout();
    toast("Signed out on all devices.", "success");
    navigate("/", { replace: true });
  });
  const exportData = () => run("export", async () => {
    const json = JSON.stringify(await api("/me/export"), null, 2);
    setExported(json);
    const url = URL.createObjectURL(new Blob([json], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "breez-my-data.json";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  });
  const doDelete = () => run("delete", async () => {
    setDelError(null);
    leaving.current = true;
    try {
      await deleteAccount(delInput.trim() === "DELETE" ? { confirm: "DELETE" } : { password: delInput });
    } catch (e) {
      leaving.current = false;
      setDelError(e.message);
      return;
    }
    toast("Your account and data were deleted.", "success");
    navigate("/", { replace: true });
  });
  const pwOk = strong(pw.next) && pw.next === pw.confirm && pw.current.length > 0;
  const err = "t-caption !text-[#EF4444]";

  return (
    <div data-testid="account-screen">
      <ScreenHeader testId="account-screen-back" label="BREEZ" fallback="/mystyle" />
      <Page wide className="pb-12">
        <h1 className="t-h1 mb-4">Account</h1>
        <div className="grid gap-4 md:grid-cols-2">
          <Section title="Profile" testId="account-profile">
            <p data-testid="account-email" className="t-body">Signed in as {user.email}</p>
            <Field testId="account-name-input" label="Display name" value={name} onChange={setName} />
            <Button testId="account-name-save" title="Save name" loading={busy === "name"} disabled={!name.trim() || name.trim() === user.name} onClick={saveName} />
          </Section>
          <Section title="Change email" note="Needs your current password.">
            <Field testId="account-new-email" label="New email" value={email} onChange={setEmail} placeholder="you@example.com" type="email" />
            <Field testId="account-email-password" label="Current password" value={emailPw} onChange={setEmailPw} type="password" />
            <Button testId="account-email-save" title="Change email" loading={busy === "email"} disabled={!email.includes("@") || !emailPw} onClick={changeEmail} />
          </Section>
          <Section title="Change password" note={PW_HINT}>
            <Field testId="account-current-password" label="Current password" value={pw.current} onChange={(current) => setPw({ ...pw, current })} type="password" />
            <Field testId="account-new-password" label="New password" value={pw.next} onChange={(next) => setPw({ ...pw, next })} type="password" />
            <Field testId="account-confirm-password" label="Confirm new password" value={pw.confirm} onChange={(confirm) => setPw({ ...pw, confirm })} type="password" />
            {pw.next && !strong(pw.next) ? <p data-testid="account-password-rule" className={err}>{PW_HINT}</p> : null}
            {pw.confirm && pw.next !== pw.confirm ? <p data-testid="account-password-mismatch" className={err}>Passwords don&apos;t match.</p> : null}
            <Button testId="account-password-save" title="Change password" loading={busy === "pw"} disabled={!pwOk} onClick={changePw} />
          </Section>
          <div className="flex flex-col gap-4">
            <Section title="Sessions">
              <Button testId="account-signout" title="Sign out" icon={LogOut} variant="outline" onClick={async () => { leaving.current = true; await logout(); navigate("/", { replace: true }); }} />
              <Button testId="account-signout-all" title="Sign out everywhere" icon={MonitorSmartphone} variant="outline" loading={busy === "all"} onClick={signOutAll} />
            </Section>
            <Section title="Export my data" note="A JSON file with your profile, saved styles and scan estimates. No photos — we never store them.">
              <Button testId="account-export" title="Download my data" icon={Download} variant="outline" loading={busy === "export"} onClick={exportData} />
              {exported ? <pre data-testid="account-export-preview" className="t-caption max-h-40 overflow-auto whitespace-pre-wrap">{exported}</pre> : null}
            </Section>
            <Section title="Privacy">
              <Button testId="account-privacy-centre" title="Privacy centre" icon={ShieldCheck} variant="ghost" onClick={() => navigate("/privacy")} />
            </Section>
          </div>
          <Section title="Delete account" testId="account-delete-section" note="Permanently deletes your account, hair profile, scan estimates and saved styles from our servers, and clears this browser. This can't be undone.">
            {delStep === 0 ? (
              <Button testId="account-delete-start" title="Delete my account" icon={Trash2} variant="outline" onClick={() => setDelStep(1)} />
            ) : (
              <div className="flex flex-col gap-3">
                <Field testId="account-delete-input" label="Type DELETE or enter your password" value={delInput} onChange={setDelInput} />
                {delError ? <p data-testid="account-delete-error" role="alert" className="t-body !text-[#EF4444]">{delError}</p> : null}
                <div className="flex gap-3">
                  <Button testId="account-delete-confirm" title="Delete forever" icon={Trash2} loading={busy === "delete"} disabled={!delInput.trim()} onClick={doDelete} className="flex-1 !bg-[#EF4444]" />
                  <Button testId="account-delete-cancel" title="Cancel" variant="outline" onClick={() => { setDelStep(0); setDelInput(""); setDelError(null); }} className="flex-1" />
                </div>
              </div>
            )}
          </Section>
        </div>
      </Page>
    </div>
  );
}
